import {
  ChargeQuatationStatus,
  FreezeRequestStatus,
  OnlinePaymentStatus,
  SignUpStatus,
} from '@fitness-force/enum';
import { formatApiErrorMessage } from '@fitness-force/api/error-message';
import type { GetOrderStatusOfQuotation } from '@fitness-force/models';

export type SignupPaymentPollOutcome = 'success' | 'failed' | 'pending';

export function isSignupPaymentTerminalStatus(status: SignUpStatus | string): boolean {
  return (
    status === SignUpStatus.SignUpSuccessfull ||
    status.toString() === SignUpStatus[SignUpStatus.SignUpSuccessfull] ||
    status === SignUpStatus.SignUpFailed ||
    status.toString() === SignUpStatus[SignUpStatus.SignUpFailed] ||
    status === SignUpStatus.SignUpExpired ||
    status.toString() === SignUpStatus[SignUpStatus.SignUpExpired]
  );
}

export function isSignupSuccessfulStatus(status: SignUpStatus | string): boolean {
  return (
    status === SignUpStatus.SignUpSuccessfull ||
    status.toString() === SignUpStatus[SignUpStatus.SignUpSuccessfull]
  );
}

export function normalizeOnlinePaymentStatus(
  status: OnlinePaymentStatus | string | undefined | null
): OnlinePaymentStatus | undefined {
  if (!status) {
    return undefined;
  }

  const normalized = status.toString();
  if (
    normalized === OnlinePaymentStatus.Success ||
    normalized === OnlinePaymentStatus.Failed ||
    normalized === OnlinePaymentStatus.InProgress
  ) {
    return normalized as OnlinePaymentStatus;
  }

  return undefined;
}

export function evaluateOnlinePaymentPollOutcome(
  response: GetOrderStatusOfQuotation
): SignupPaymentPollOutcome | null {
  const onlineStatus = normalizeOnlinePaymentStatus(response.online_payment_status);
  if (!onlineStatus) {
    return null;
  }

  switch (onlineStatus) {
    case OnlinePaymentStatus.Success:
      return 'success';
    case OnlinePaymentStatus.Failed:
      return 'failed';
    case OnlinePaymentStatus.InProgress:
      return 'pending';
    default: {
      const unhandledStatus: never = onlineStatus;
      return unhandledStatus;
    }
  }
}

export function getOnlinePaymentFailureReason(
  response: GetOrderStatusOfQuotation
): string | undefined {
  const reason = response.online_payment_failure_reason?.trim();
  if (!reason) {
    return undefined;
  }

  return formatApiErrorMessage(reason) ?? undefined;
}

export interface EvaluateSignupPaymentPollOutcomeOptions {
  /** When true, only online_payment_status determines terminal success/failure. */
  requireOnlinePaymentStatus?: boolean;
}

export function isChargeOrderSuccessfulStatus(
  status: ChargeQuatationStatus | string
): boolean {
  return (
    status === ChargeQuatationStatus.Successfull ||
    status.toString() === ChargeQuatationStatus[ChargeQuatationStatus.Successfull] ||
    status === 'Successfull'
  );
}

export function isChargeOrderFailedStatus(
  status: ChargeQuatationStatus | string
): boolean {
  return (
    status === ChargeQuatationStatus.Failed ||
    status.toString() === ChargeQuatationStatus[ChargeQuatationStatus.Failed] ||
    status === 'Failed' ||
    status === ChargeQuatationStatus.Expired ||
    status.toString() === ChargeQuatationStatus[ChargeQuatationStatus.Expired] ||
    status === 'Expired'
  );
}

export function evaluateFreezeOrderPollOutcome(
  status: FreezeRequestStatus | string
): SignupPaymentPollOutcome | null {
  if (
    status === FreezeRequestStatus.FREEZE_APPLIED ||
    status.toString() === FreezeRequestStatus[FreezeRequestStatus.FREEZE_APPLIED] ||
    status === FreezeRequestStatus.APPROVED_PAYMENT_RECEIVED ||
    status.toString() ===
      FreezeRequestStatus[FreezeRequestStatus.APPROVED_PAYMENT_RECEIVED] ||
    status === FreezeRequestStatus.APPROVED_NO_PAYMENT_NEEDED ||
    status.toString() ===
      FreezeRequestStatus[FreezeRequestStatus.APPROVED_NO_PAYMENT_NEEDED]
  ) {
    return 'success';
  }

  if (
    status === FreezeRequestStatus.REJECTED ||
    status.toString() === FreezeRequestStatus[FreezeRequestStatus.REJECTED] ||
    status === FreezeRequestStatus.VOID ||
    status.toString() === FreezeRequestStatus[FreezeRequestStatus.VOID] ||
    status === FreezeRequestStatus.EXPIRED ||
    status.toString() === FreezeRequestStatus[FreezeRequestStatus.EXPIRED]
  ) {
    return 'failed';
  }

  return null;
}

export function evaluateSignupPaymentPollOutcome(
  response: GetOrderStatusOfQuotation,
  options?: EvaluateSignupPaymentPollOutcomeOptions
): SignupPaymentPollOutcome {
  const onlineOutcome = evaluateOnlinePaymentPollOutcome(response);
  if (onlineOutcome !== null) {
    return onlineOutcome;
  }

  if (options?.requireOnlinePaymentStatus) {
    return 'pending';
  }

  const chargeOrder = response?.chargeOrderStatus;
  if (chargeOrder?.status != null) {
    if (isChargeOrderSuccessfulStatus(chargeOrder.status)) {
      return 'success';
    }
    if (isChargeOrderFailedStatus(chargeOrder.status)) {
      return 'failed';
    }
    return 'pending';
  }

  const freezeOrder = response?.freezeOrderStatus;
  if (freezeOrder?.frz_req_status != null) {
    const freezeOutcome = evaluateFreezeOrderPollOutcome(
      freezeOrder.frz_req_status
    );
    if (freezeOutcome !== null) {
      return freezeOutcome;
    }
    return 'pending';
  }

  const signupResponse = response?.signUpOrderSummaryResponse;
  if (!signupResponse) {
    return 'pending';
  }

  const status = signupResponse.status;
  if (isSignupSuccessfulStatus(status)) {
    return 'success';
  }

  if (
    status === SignUpStatus.SignUpFailed ||
    status.toString() === SignUpStatus[SignUpStatus.SignUpFailed] ||
    status === SignUpStatus.SignUpExpired ||
    status.toString() === SignUpStatus[SignUpStatus.SignUpExpired]
  ) {
    return 'failed';
  }

  return 'pending';
}

export function resolveSignupTimelineStatusAfterPayment(
  response: GetOrderStatusOfQuotation,
  signupStatus: SignUpStatus | string
): SignUpStatus {
  if (isSignupSuccessfulStatus(signupStatus)) {
    return SignUpStatus.SignUpSuccessfull;
  }

  const onlineOutcome = evaluateOnlinePaymentPollOutcome(response);
  if (onlineOutcome === 'success') {
    return SignUpStatus.AwaitingPayment;
  }

  return signupStatus as SignUpStatus;
}
